import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dumbbell, Activity, Trophy, Heart, TrendingUp, Target, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import AOS from 'aos';
import 'aos/dist/aos.css';

const Services = () => {
  useEffect(() => {
    AOS.init({
      duration: 600,
      easing: 'ease-in-out',
      once: true
    });
  }, []);

  const services = [
    {
      icon: Dumbbell,
      title: 'Strength & Conditioning',
      description: 'Build lean muscle mass, increase overall strength, and improve functional fitness with structured progressive overload programs.',
      benefits: [
        'Customized workout plans based on your experience level',
        'Proper form coaching to prevent injuries',
        'Progressive programming for continuous gains',
        'Strength assessments and tracking'
      ],
      image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438'
    },
    {
      icon: TrendingUp,
      title: 'Weight Loss Programs',
      description: 'Achieve sustainable fat loss through evidence-based training and personalized nutrition guidance tailored to your lifestyle.',
      benefits: [
        'Customized calorie and macronutrient targets',
        'Metabolic conditioning workouts',
        'Weekly progress tracking and adjustments',
        'Lifestyle and habit coaching'
      ],
      image: 'https://images.pexels.com/photos/6551068/pexels-photo-6551068.jpeg'
    },
    {
      icon: Trophy,
      title: 'Bodybuilding Prep',
      description: 'Competition-level coaching for aspiring bodybuilders. Benefit from my experience competing in elite shows like Vancity Showdown.',
      benefits: [
        'Periodized training for hypertrophy',
        'Peak week strategies and posing practice',
        'Competition nutrition protocols',
        'Stage presentation coaching'
      ],
      image: 'https://images.unsplash.com/photo-1605822335686-ce034adfda7d'
    },
    {
      icon: Activity,
      title: 'Marathon Training',
      description: 'Comprehensive endurance training programs for runners of all levels, from your first 5K to marathon distance.',
      benefits: [
        'Structured training plans with periodization',
        'Running form analysis and correction',
        'Injury prevention strategies',
        'Race day preparation and nutrition'
      ],
      image: 'https://images.pexels.com/photos/2803158/pexels-photo-2803158.jpeg'
    },
    {
      icon: Heart,
      title: 'Lifestyle Coaching',
      description: 'Holistic approach to health and wellness, focusing on sustainable habits that improve your overall quality of life.',
      benefits: [
        'Stress management techniques',
        'Sleep optimization strategies',
        'Nutrition education and meal planning',
        'Accountability and motivation support'
      ],
      image: 'https://images.unsplash.com/photo-1608877906479-96f265ab5aab'
    },
    {
      icon: Target,
      title: 'Muscle Gain Programs',
      description: 'Strategic hypertrophy training designed to help you build quality muscle mass efficiently and effectively.',
      benefits: [
        'High-volume training protocols',
        'Optimal nutrition for muscle growth',
        'Recovery and rest day planning',
        'Supplement guidance and recommendations'
      ],
      image: 'https://images.unsplash.com/photo-1690731033723-ad718c6e585a'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="display-large" data-aos="fade-up">Training Programs</h1>
          <p className="body-large" data-aos="fade-up" data-aos-delay="100">
            Personalized training programs designed to help you achieve your specific fitness goals
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-list-section">
        <div className="container">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isEven = index % 2 === 0;
            
            // Create anchor ID from title
            const anchorId = service.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
            
            return (
              <div 
                key={index}
                id={anchorId}
                className={`service-detail-card ${isEven ? 'service-card-left' : 'service-card-right'}`}
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="service-detail-image">
                  <img src={service.image} alt={service.title} />
                </div>
                <div className="service-detail-content">
                  <div className="service-icon-wrapper">
                    <IconComponent size={40} className="service-icon" />
                  </div>
                  <h2 className="heading-2">{service.title}</h2>
                  <p className="body-large">{service.description}</p>
                  
                  <div className="service-benefits">
                    <h3 className="heading-3">What You Get:</h3>
                    <ul className="benefits-list">
                      {service.benefits.map((benefit, i) => (
                        <li key={i} className="body-medium">{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* How It Works */}
      <section className="process-section">
        <div className="container">
          <h2 className="heading-1 text-center" data-aos="fade-up">How It Works</h2>
          <div className="process-grid">
            <div className="process-step" data-aos="fade-up" data-aos-delay="100">
              <div className="process-number">1</div>
              <h3 className="heading-3">Free Consultation</h3>
              <p className="body-medium">
                We'll discuss your goals, assess your current fitness level, and determine 
                the best program for your needs.
              </p>
            </div>
            
            <div className="process-step" data-aos="fade-up" data-aos-delay="200">
              <div className="process-number">2</div>
              <h3 className="heading-3">Customized Plan</h3>
              <p className="body-medium">
                I'll create a personalized training and nutrition plan designed specifically 
                for your goals and lifestyle.
              </p>
            </div>
            
            <div className="process-step" data-aos="fade-up" data-aos-delay="300">
              <div className="process-number">3</div>
              <h3 className="heading-3">Start Training</h3>
              <p className="body-medium">
                Begin your transformation with expert guidance, ongoing support, and regular 
                progress tracking.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="container">
          <div className="services-cta-content" data-aos="fade-up">
            <h2 className="display-medium">Ready to Get Started?</h2>
            <p className="body-large">
              Book your free consultation today and let's create a plan that works for you.
            </p>
            <Link to="/contact">
              <Button className="btn-cta">
                Book Free Consultation
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
